import { PlusOutlined } from '@ant-design/icons';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useRelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { useBasicDndDragger } from '@framework/ui/core/dnd/hooks/useBasicDndDragger.ts';
import { useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { type IWidgetAdvancedOption } from '../../../../Widget.ts';
import { AdvancedSelectSortableWidgetOptionItem } from '../../common/option-list/AdvancedSelectSortableWidgetOptionItem.tsx';
import { AdvancedSelectImportCsvOptionsButton } from './AdvancedSelectImportCsvOptionsButton.tsx';
import { AdvancedSelectOptionValueEditDrawer } from './AdvancedSelectOptionValueEditDrawer.tsx';
import { AdvancedSelectOptionValueView } from './AdvancedSelectOptionValueView.tsx';

export interface IAdvancedSelectOptionItemListProps {
  name: string;
}

/**
 * Componente con D'n'D per la configurazione della lista delle opzioni
 * dei widget AdvancedSelect (singola e multipla). Gestisce opzioni con valori multipli
 * basati sulle colonne configurate.
 */
export function AdvancedSelectOptionItemList(
  props: IAdvancedSelectOptionItemListProps,
) {
  const { name } = props;

  const editor = useRelationEditor<IWidgetAdvancedOption>(name);
  const [index, setIndex] = useState<number | null>(null);
  const { sensors, handleDragStart, handleDragEnd, itemIds, active } =
    useBasicDndDragger(editor);

  // Ottieni le colonne configurate
  const columns = useWatch({ name: 'columns' });

  const hasColumns = useMemo(() => {
    return columns && columns.length > 0;
  }, [columns]);

  const incrementIndex = useCallback(() => {
    setIndex(prevIndex => (prevIndex !== null ? prevIndex + 1 : 0));
  }, []);

  return (
    <FormItem name={name} label={null}>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {hasColumns &&
            editor.items.map((item, index) => (
              <AdvancedSelectSortableWidgetOptionItem
                item={item}
                uid={item.__uid ?? ''}
                key={item.__uid}
                active={active}
                columns={columns}
                incrementIndex={incrementIndex}
                editor={editor}
              />
            ))}
          <DragOverlay>
            <AdvancedSelectOptionValueView item={active} columns={columns} />
          </DragOverlay>
        </SortableContext>
      </DndContext>

      {/* Pulsante di aggiunta opzione */}
      <RowElement gutter={8}>
        <AdvancedSelectOptionValueEditDrawer
          editor={editor}
          index={index}
          setIndex={setIndex}
          columns={columns}
          buttonProps={{
            type: 'dashed',
            size: 'large',
            icon: <PlusOutlined aria-hidden="true" />,
            block: true,
            children: 'Aggiungi Opzione',
            disabled: !hasColumns,
          }}
        />
      </RowElement>

      {/* Pulsante per import da file CSV */}
      <AdvancedSelectImportCsvOptionsButton />
    </FormItem>
  );
}
